import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Manager {
  id: number
  name: string
}

interface StateOption {
  value: string
  label: string
}

interface CompanyData {
  name: string
  nickname: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  phone: string
  email: string
  website: string
  color1: string
  color2: string
  manager_id: number | null
}

interface Props {
  company: CompanyData
  managers: Manager[]
  states: StateOption[]
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/companies" },
  { title: "Companies", href: "/admin/companies" },
  { title: "Add", href: "/admin/companies/new" },
]

export default function New({ company, managers, states, errors }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    name: company.name,
    nickname: company.nickname,
    address: company.address,
    address2: company.address2,
    city: company.city,
    state: company.state,
    zip: company.zip,
    phone: company.phone,
    email: company.email,
    website: company.website,
    color1: company.color1,
    color2: company.color2,
    manager_id: company.manager_id ?? "",
  })

  transform((data) => ({ company: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/admin/companies")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Company" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Company</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl">
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="mb-1 block font-medium">
                Company Name
              </label>
              <input
                type="text"
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={100}
                required
              />
              {errors?.name && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.name.join(", ")}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="nickname" className="mb-1 block font-medium">
                Nickname
              </label>
              <input
                type="text"
                id="nickname"
                value={data.nickname}
                onChange={(e) => setData("nickname", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
                required
              />
              {errors?.nickname && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.nickname.join(", ")}
                </p>
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="address" className="mb-1 block font-medium">
                Address
              </label>
              <input
                type="text"
                id="address"
                value={data.address}
                onChange={(e) => setData("address", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="address2" className="mb-1 block font-medium">
                Address Line 2
              </label>
              <input
                type="text"
                id="address2"
                value={data.address2}
                onChange={(e) => setData("address2", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="city" className="mb-1 block font-medium">
                City
              </label>
              <input
                type="text"
                id="city"
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="state" className="mb-1 block font-medium">
                State
              </label>
              <select
                id="state"
                value={data.state}
                onChange={(e) => setData("state", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Choose</option>
                {states.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="zip" className="mb-1 block font-medium">
                Zip
              </label>
              <input
                type="text"
                id="zip"
                value={data.zip}
                onChange={(e) => setData("zip", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={10}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="manager_id" className="mb-1 block font-medium">
              Manager
            </label>
            <select
              id="manager_id"
              value={data.manager_id}
              onChange={(e) =>
                setData(
                  "manager_id",
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value="">Choose</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="mb-1 block font-medium">
                Phone
              </label>
              <input
                type="text"
                id="phone"
                value={data.phone}
                onChange={(e) => setData("phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-1 block font-medium">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="website" className="mb-1 block font-medium">
              Website
            </label>
            <input
              type="text"
              id="website"
              value={data.website}
              onChange={(e) => setData("website", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
          </div>

          <h3 className="mb-2 text-lg font-semibold">Layout</h3>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="color1" className="mb-1 block font-medium">
                Color 1: {data.color1}
              </label>
              <input
                type="color"
                id="color1"
                value={data.color1}
                onChange={(e) => setData("color1", e.target.value)}
                className="h-[75px] w-[75px]"
              />
            </div>
            <div>
              <label htmlFor="color2" className="mb-1 block font-medium">
                Color 2: {data.color2}
              </label>
              <input
                type="color"
                id="color2"
                value={data.color2}
                onChange={(e) => setData("color2", e.target.value)}
                className="h-[75px] w-[75px]"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Add
            </button>
            <Link
              href="/admin/companies"
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
